import { Injectable } from '@angular/core';
import { DataService } from '../services/data.service';
import { BlobType } from '../enums/blob-storage/blob-type.enum';
import { BehaviorSubject, defer, firstValueFrom, lastValueFrom, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { BlobUploadCallbacks } from '../services/http.service';

interface BlobUploadToken {
  UriToken: string;
  FileName: string;
}

export type BlobUploadFile = Blob & { readonly name: string };

@Injectable({
  providedIn: 'root',
})
export class AzureBlobStorageService extends DataService {
  private progressSource = new BehaviorSubject<string>('');
  progress$ = this.progressSource.asObservable();

  constructor() {
    super();
  }

  async upload(file: File, type: BlobType, fileExtension?: string): Promise<{ uriToken: string; blobFileName: string }> {
    const response = await firstValueFrom(this.getUriToken(file, type, fileExtension));
    await lastValueFrom(this.http.putBlob(response.UriToken, file, progress => this.progressSource.next(progress)));

    return {
      uriToken: response.UriToken,
      blobFileName: response.FileName
    };
  }

  /** Emits the signed URL after commit; unsubscription cancels requests and pending retries. */
  uploadInChunks(
    file: BlobUploadFile,
    type: BlobType.INDEXED_DB | BlobType.VIDEO,
    callbacks: BlobUploadCallbacks = {}
  ): Observable<string> {
    return defer(() => {
      const uploadCallbacks: BlobUploadCallbacks = {
        onProgress: callbacks.onProgress,
        onDiagnostic: diagnostic => callbacks.onDiagnostic?.({ ...diagnostic, fileSize: file.size }),
      };

      return this.getUriToken(file, type, undefined, uploadCallbacks).pipe(
        switchMap(response => this.http.putBlobInChunks(
          response.UriToken,
          file,
          () => this.getWriteToken(type, response.FileName, uploadCallbacks, 'commit-token').pipe(map(token => token.UriToken)),
          uploadCallbacks
        ).pipe(map(() => response.UriToken)))
      );
    });
  }

  private getUriToken(
    file: BlobUploadFile,
    type: BlobType,
    fileExtension?: string,
    callbacks: BlobUploadCallbacks = {}
  ): Observable<BlobUploadToken> {
    switch (type) {
      case BlobType.PHOTO:
        return this.getPhotoUriToken(file);
      case BlobType.VIDEO:
        return this.getWriteToken(type, file.name, callbacks);
      case BlobType.INDEXED_DB:
        return this.getWriteToken(type, undefined, callbacks);
      case BlobType.ODOMETER_READING:
        return this.getOdometerReadingUriToken(fileExtension);
    }
  }

  private getPhotoUriToken(file: BlobUploadFile): Observable<BlobUploadToken> {
    return this.http.post<{ Result: BlobUploadToken }>(this.BLOB_STORAGE_ENDPOINTS[BlobType.PHOTO], { FileName: file.name, isThumb: false }).pipe(
      map(response => response.Result)
    );
  }

  private getWriteToken(
    type: BlobType.INDEXED_DB | BlobType.VIDEO,
    filename: string | undefined,
    callbacks: BlobUploadCallbacks,
    stage: 'token' | 'commit-token' = 'token'
  ): Observable<BlobUploadToken> {
    const parameters = filename === undefined ? {} : { filename };
    const url = this.http.buildUrl(this.BLOB_STORAGE_ENDPOINTS[type].write, parameters);
    return this.http.getUploadToken(url, callbacks, stage);
  }

  private getOdometerReadingUriToken(fileExtension?: string): Observable<BlobUploadToken> {
    return this.http.post<{
      fileName: string;
      uriToken: string;
      expiryTime: string;
    }>(this.BLOB_STORAGE_ENDPOINTS[BlobType.ODOMETER_READING], {
      fileExtension: fileExtension
    }).pipe(map(response => ({
      UriToken: response.uriToken,
      FileName: response.fileName,
    })));
  }
}
